import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { SparkPoint } from "./shared";

// Each trend's `comparison` is what its change is measured against ("vs Sep'26", "vs prior 12 months"), shown beside the change.
export interface ConcentrationRow {
  label: string; // "> <approved Annual Review Threshold>" (e.g. "> $2M"), "> $500K", "Franchise"
  pctOwned: number;
  trend: { value: string; direction: "up" | "down" | "flat"; comparison?: string | null };
  linkTo: string;
}

export interface OfficeRow {
  office: string;
  contracts: number;
  ni: number;
}

export interface ChannelRow {
  channel: string;
  contracts: number;
  ni: number;
  offices: OfficeRow[];
}

export interface StateRow {
  state: string;
  ni: number;
  contracts: number;
}

// A type alias (not an interface) so rows satisfy GroupedBarChart's Record<string, unknown>.
export type DelinquencyYearPoint = {
  year: string;
  d1to29: number;
  d30to89: number;
  d90plus: number;
  nonAccruing: number;
};

export interface PerformanceYearRow {
  year: string;
  nonPerformingNI: number;
  performingNI: number;
}

export interface GrowthHighlight {
  label: string;
  value: string;
  trend: { value: string; direction: "up" | "down" | "flat"; comparison?: string | null };
}

// /net-investment used to be one endpoint returning everything this page needed in one
// NetInvestmentSummary. It's now 7 small per-card endpoints (see the backend's
// app/repository/growth_exposure.py for why: one request computing all of this took ~22-37s
// against the real data, past the gateway's 30s timeout). Each hook below fetches its own
// card independently -- react-query fires all of them concurrently when NetInvestment.tsx
// calls every hook, so the page's perceived load time is bounded by the single slowest card,
// not the sum of all of them, and each card can show its own loading/error state.

export interface NetInvestmentHeadline {
  ownedNI: { current: number; sparkline: SparkPoint[] };
  activeContracts: {
    current: number;
    trend: { value: string; direction: "up" | "down" | "flat"; comparison?: string | null };
  };
  niTrend: Array<{ month: string; ownedNI: number }>;
}

export function useNetInvestmentHeadline() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "headline", ticket ?? "all"],
    queryFn: () => apiGet<NetInvestmentHeadline>("/net-investment/headline", { ticket }),
  });
}

export function useNetInvestmentConcentration() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "concentration", ticket ?? "all"],
    queryFn: () => apiGet<{ concentration: ConcentrationRow[] }>("/net-investment/concentration", { ticket }),
  });
}

export function useNetInvestmentChannels() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "channels", ticket ?? "all"],
    queryFn: () => apiGet<{ channels: ChannelRow[] }>("/net-investment/channels", { ticket }),
  });
}

export function useNetInvestmentTopStates() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "top-states", ticket ?? "all"],
    queryFn: () => apiGet<{ topStates: StateRow[] }>("/net-investment/top-states", { ticket }),
  });
}

export function useNetInvestmentDelinquency() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "delinquency", ticket ?? "all"],
    queryFn: () => apiGet<{ delinquency: DelinquencyYearPoint[] }>("/net-investment/delinquency", { ticket }),
  });
}

export function useNetInvestmentPerformance() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "performance", ticket ?? "all"],
    queryFn: () => apiGet<{ performance: PerformanceYearRow[] }>("/net-investment/performance", { ticket }),
  });
}

export function useNetInvestmentGrowthHighlights() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["net-investment", "growth-highlights", ticket ?? "all"],
    queryFn: () =>
      apiGet<{ growthHighlights: GrowthHighlight[] }>("/net-investment/growth-highlights", { ticket }),
  });
}
